import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { Config } from "@config";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { GA4EventParameters } from "@utils/google-analytics-4.type";
import {
	Alert,
	AlertHeading,
	HtmlBlock,
	LandingContainer,
	PromisePool,
	generateUrlParams,
	getUrlParameters,
} from "bm-ui-kit";
import lzString from "lz-string";
import type { ReactNode } from "react";
import { Col, Container } from "react-bootstrap";
import { InView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";
import type { AbstractLeadFormProps } from "../AbstractLeadForm/AbstractLeadForm";
import type { ProductType } from "../AbstractLeadForm/types";
import type { AbstractResponseItem } from "./types";

export interface AbstractDataPageState<ResponseItem> extends MeasureComponentState {
	loadingStatus: "loading" | "error" | "done";
	responseItem: ResponseItem;
}

export abstract class AbstractDataPage<
	SearchParams extends Record<string, any>,
	ResponseItem extends AbstractResponseItem,
	Props extends MeasureComponentProps = MeasureComponentProps,
	State extends AbstractDataPageState<ResponseItem> = AbstractDataPageState<ResponseItem>,
> extends MeasureComponent<Props, State> {
	protected readonly promisePool: PromisePool = new PromisePool();
	protected productId: string;
	protected searchParams: SearchParams;
	protected buttonId: string;

	public constructor(props: Props) {
		super(props);

		const params = getUrlParameters();

		this.productId = params.ID;
		this.buttonId = GoogleAnalytics4.getButtonId(this.getSessionStorageName());
	}

	public componentDidMount(): void {
		super.componentDidMount();

		const params = getUrlParameters();

		if (params.d) {
			this.searchParams = JSON.parse(lzString.decompressFromEncodedURIComponent(params.d)) as SearchParams;
		} else {
			this.searchParams = params as SearchParams;

			if (Array.isArray(window.BM_DATAPAGE) && window.BM_DATAPAGE[0]) {
				this.setState({
					responseItem: window.BM_DATAPAGE[0] as ResponseItem,
				});
			}
		}

		this.promisePool
			.add(this.loadProductData(this.searchParams, this.productId))
			.then((responseItem: ResponseItem) => {
				if (!responseItem) {
					throw Error("Missing Data");
				}

				this.setState(
					{
						responseItem,
						loadingStatus: "done",
					},
					() => {
						this.onProductDataLoaded();
					},
				);
			})
			.catch(() => {
				if (Array.isArray(window.BM_DATAPAGE) && window.BM_DATAPAGE[0]) {
					this.setState({
						responseItem: window.BM_DATAPAGE[0] as unknown as ResponseItem,
					});

					return;
				}

				this.setState({
					loadingStatus: "error",
				});

				this.promisePool.addTimeout().then(() => {
					this.redirectToCalculator();
				});
			});
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected redirectToCalculator(): void {
		window.location.href = `${window.location.href.split("/adatlap")[0]}?${generateUrlParams(this.searchParams)}`;
	}

	protected onProductDataLoaded(): void {}

	protected abstract loadProductData(searchParams: SearchParams, id: string): Promise<ResponseItem>;

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			loadingStatus: "loading",
			responseItem: undefined,
		} as unknown as State;
	}

	public render(): ReactNode {
		const { loadingStatus, wasVisible } = this.state;

		if (Config.isSsr() || loadingStatus === "loading") {
			return (
				<div className={this.getClassName()}>
					<Container>
						<div className="my-4 pb-2 border-bottom">
							<div className="h1 my-0">
								<Skeleton count={1} />
							</div>
						</div>
					</Container>
					<LandingContainer>
						<Skeleton count={15} height="30px" />
					</LandingContainer>
				</div>
			);
		}

		if (loadingStatus === "error") {
			return (
				<LandingContainer>
					<Alert icon="auto" variant="danger" size="lg">
						<AlertHeading>Sajnos a keresett termék már nem érhető el!</AlertHeading>
						Hamarosan visszairányítjuk a kalkulátorunkhoz...
					</Alert>
				</LandingContainer>
			);
		}

		return (
			<div className={this.getClassName()}>
				{this.renderHeader()}
				<InView {...GoogleAnalytics4.getInViewProps()} skip={wasVisible} onChange={this.onVisibilityChange}>
					{this.renderContent()}
				</InView>
			</div>
		);
	}

	protected renderHeader(): ReactNode {
		const { responseItem } = this.state;
		document.title = `${responseItem.name} | Bankmonitor.hu`;

		return (
			<Container>
				<div className="d-flex flex-wrap align-items-end my-4 pb-2 border-bottom">
					<Col>
						<h1 className="my-0">{responseItem.name}</h1>
						{responseItem.subType && <div className="h2">{responseItem.subType}</div>}
					</Col>
				</div>
			</Container>
		);
	}

	protected abstract renderContent(): ReactNode;

	protected renderMarketingBlock(): ReactNode {
		const { responseItem } = this.state;
		const marketingText = responseItem.marketing?.formattedText;

		if (marketingText) {
			return (
				<Alert icon={<i className="fab bm-badge-icon fa-2x" />} variant="warning">
					<HtmlBlock className="border-left pl-3" html={marketingText} />
				</Alert>
			);
		}

		return null;
	}

	protected getLeadProps(): AbstractLeadFormProps<any, any, SearchParams> {
		return {
			searchParams: this.searchParams,
			colorScheme: "light",
			bottomLogo: false,
			ga4Event: this.getCommonEventParameters(),
		};
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		return {
			buttonId: this.buttonId,
			...super.getCommonEventParameters(),
		};
	}

	protected getClassName(): string {
		return "data-page";
	}

	protected abstract getProductType(): ProductType;

	private getSessionStorageName(): string {
		return `button_id_${this.getProductType()}_${this.productId}`;
	}
}
